import PublicLayout from "@/components/layout/PublicLayout";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, Fragment } from "react";
import { Check, CheckCircle, ArrowUpRight, ShieldCheck as ShieldCheckIcon, Lock, Receipt, Zap, Repeat, Eye } from "lucide-react";
import { createCheckout } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import InvestorDashboardPreview from "@/components/investor/preview/InvestorDashboardPreview";
import InvestorComparePreview, {
    COMPARE_COMBOS,
    CompareTiersButton,
} from "@/components/investor/preview/InvestorComparePreview";
import { track } from "@/lib/analytics";
import SlidingTabs from "@/components/ui/sliding-tabs";

const ONE_TIME = [
    {
        id: "summary",
        name: "Inspection Summary",
        badge: "Standard",
        price: "$49",
        suffix: "one-time",
        tagline: "Understand the inspection. Reduce overwhelm. Prioritize what matters.",
        features: [
            "Organized inspection findings",
            "Priority & severity tagging",
            "Estimated Professional Repair Exposure",
            "Plain-English explanations",
            "Basic next-step guidance",
            "Downloadable PDF",
            "Shareable view-only link",
        ],
    },
    {
        id: "closing_strategy",
        name: "Closing Strategy",
        badge: "Premium · Most popular",
        price: "$99",
        suffix: "one-time",
        highlight: true,
        tagline: "Negotiation intelligence. Strategic sequencing. Decide what to DO.",
        features: [
            "Everything in Inspection Summary",
            "Negotiation leverage scoring",
            "Seller concession strategy guidance",
            "Strategic issue grouping & sequencing",
            "Financing & insurance flagging",
            "Questions to ask seller and contractors",
            "Contingency timing intelligence",
            "Buyer action roadmap",
            "Quote-request workflow access",
            "Negotiation-ready PDF export",
        ],
    },
];

// Side-by-side feature delta used in the comparison table below the cards.
const COMPARISON_ROWS = [
    { group: "Findings", rows: [
        ["Issue Summary", true, true],
        ["Priority & severity tagging", true, true],
        ["Estimated Professional Repair Exposure", true, true],
        ["Localized cost reasoning by ZIP", true, true],
        ["Plain-English explanations", true, true],
    ]},
    { group: "Negotiation Intelligence", rows: [
        ["Negotiation leverage scoring", false, true],
        ["Seller concession strategy guidance", false, true],
        ["Strategic issue grouping", false, true],
        ["Buyer action roadmap (sequenced next steps)", false, true],
        ["Financing & insurance risk flags", false, true],
    ]},
    { group: "Question Prep", rows: [
        ["Questions to ask contractor", false, true],
        ["Questions to ask seller", false, true],
        ["Specialist follow-up recommendations", true, true],
    ]},
    { group: "Workflow & Exports", rows: [
        ["Downloadable PDF", true, true],
        ["Shareable view-only link", true, true],
        ["Quote request workflow", true, true],
        ["Negotiation-ready Premium PDF", false, true],
    ]},
];

const SUBS_MONTHLY = [
    {
        id: "investor_lite",
        name: "Investor Lite",
        price: "$29",
        per: "/month",
        positioning: "For occasional deal review",
        coreValue: "Help me understand inspection reports faster.",
        tagline: "For casual investors, landlords, or buyers reviewing occasional deals.",
        quota: "3 reports / month",
        features: [
            "Standard inspection report analysis",
            "ZIP-localized professional repair exposure",
            "Basic issue prioritization",
            "PDF download + secure share links",
            "Saved report history",
            "Email support",
        ],
    },
    {
        id: "investor_pro",
        name: "Investor Pro",
        price: "$79",
        per: "/month",
        highlight: true,
        badge: "Best for active investors",
        positioning: "For active investors comparing deals",
        coreValue: "Compare properties, identify repair exposure, negotiate smarter.",
        tagline: "For active investors comparing multiple properties.",
        quota: "10 reports / month",
        features: [
            "Everything in Lite",
            "Premium Closing Strategy on every report",
            "Deal comparison dashboard",
            "Negotiation leverage scoring",
            "Cross-report search + advanced filters",
            "Investor notes, pipeline statuses, quote tracking",
            "CSV export · prioritized support",
        ],
    },
    {
        id: "investor_plus",
        name: "Investor Plus",
        price: "$149",
        per: "/month",
        badge: "For high-volume workflows",
        positioning: "For high-volume investors and small teams",
        coreValue: "Manage inspection analysis across a property pipeline.",
        tagline: "For high-volume investors, wholesalers, flippers, and acquisition managers.",
        quota: "25 reports / month",
        features: [
            "Everything in Pro",
            "Portfolio / pipeline dashboard",
            "Advanced multi-property comparison",
            "Actual quote tracking + repair-exposure calibration",
            "Bulk upload queue · advanced exports",
            "Team access — coming soon",
            "Highest support priority",
        ],
    },
];

// Annual variants — same feature set; only the id, price, and per-line
// change so monthly quotas stay month-by-month. Annual users get bonus
// upload allowances + pricing lock + early access — tracked separately
// in tier_features.INVESTOR_QUOTAS_ANNUAL.
const SUBS_ANNUAL = SUBS_MONTHLY.map((m) => {
    const annualPriceMap = {
        investor_lite: {
            id: "investor_lite_annual",
            price: "$290",
            monthlyEq: "≈ $24.17/mo billed annually",
            // Annual base quota matches the monthly tier; the bonus pill
            // below surfaces the +N extra reports/mo perk separately so
            // shoppers see the upgrade reward as additive, not as a
            // different plan with a confusing new quota number.
            quota: "3 reports / month",
            quotaBonus: "+2 reports / month",
        },
        investor_pro: {
            id: "investor_pro_annual",
            price: "$790",
            monthlyEq: "≈ $65.83/mo billed annually",
            quota: "10 reports / month",
            quotaBonus: "+5 reports / month",
        },
        investor_plus: {
            id: "investor_plus_annual",
            price: "$1,490",
            monthlyEq: "≈ $124.17/mo billed annually",
            quota: "25 reports / month",
            quotaBonus: "+10 reports / month",
        },
    };
    const ann = annualPriceMap[m.id];
    return {
        ...m,
        id: ann.id,
        price: ann.price,
        per: "/year",
        monthlyEq: ann.monthlyEq,
        quota: ann.quota,
        quotaBonus: ann.quotaBonus,
        savingsBadge: "Get 2 Months Free",
    };
});

const SUBS = SUBS_MONTHLY;  // Backwards-compat for any out-of-page references.

// Feature-grouped Lite/Pro/Plus comparison. Each row: [label, lite, pro, plus].
const INVESTOR_COMPARISON = [
    { group: "Report Analysis", rows: [
        ["Standard inspection analysis", true, true, true],
        ["ZIP-localized repair exposure", true, true, true],
        ["Basic issue prioritization", true, true, true],
        ["PDF export + share links", true, true, true],
        ["Premium Closing Strategy on every report", false, true, true],
    ]},
    { group: "Negotiation Tools", rows: [
        ["Negotiation leverage scoring", false, true, true],
        ["Seller concession strategy guidance", false, true, true],
        ["Contractor & seller question lists", false, true, true],
        ["Financing & insurance flag highlights", false, true, true],
        ["Saved negotiation summaries", false, true, true],
    ]},
    { group: "Deal Comparison", rows: [
        ["Side-by-side deal comparison dashboard", false, true, true],
        ["Cross-report search", false, true, true],
        ["Advanced filters", false, true, true],
        ["Advanced multi-property comparison", false, false, true],
    ]},
    { group: "Portfolio Workflow", rows: [
        ["Private investor notes per property", false, true, true],
        ["Pipeline statuses (Reviewing → Closed)", false, true, true],
        ["Quote request workflow", false, true, true],
        ["Quote tracking dashboard", false, true, true],
        ["Repair-exposure calibration tracker", false, true, true],
        ["Portfolio / pipeline overview", false, false, true],
        ["Actual-vs-estimated quote tracking", false, false, true],
        ["Bulk upload queue", false, false, true],
        ["Team access (coming soon)", false, false, true],
    ]},
    { group: "Export & Sharing", rows: [
        ["CSV export of comparison & reports", false, true, true],
        ["Advanced portfolio + quote exports", false, false, true],
    ]},
    { group: "Support Level", rows: [
        ["Email support", true, true, true],
        ["Repair-exposure feedback channel", false, true, true],
        ["Prioritized support review", false, true, true],
        ["Highest support priority", false, false, true],
    ]},
];

function PlanCard({ plan, onBuy, onPreview, loading, index = 0 }) {
    const previewTier =
        plan.id === "investor_lite" || plan.id === "investor_lite_annual"
            ? "lite"
            : plan.id === "investor_pro" || plan.id === "investor_pro_annual"
              ? "pro"
              : plan.id === "investor_plus" || plan.id === "investor_plus_annual"
                ? "plus"
                : null;
    return (
        <div
            className={
                "bl-card p-7 md:p-8 flex flex-col relative " +
                (plan.highlight ? "border-bl-primary ring-1 ring-bl-primary/15 shadow-[0_8px_30px_-12px_rgba(20,73,73,0.18)]" : "")
            }
            data-testid={`plan-card-${plan.id}`}
            data-reveal={plan.highlight ? "emphasize" : "rise"}
            data-reveal-delay={Math.min(5, index + 1)}
        >
            {plan.highlight && plan.badge && (
                <div
                    className="absolute -top-3 left-7 bl-badge bl-badge-medium"
                    data-testid={`plan-badge-${plan.id}`}
                >
                    {plan.badge}
                </div>
            )}
            {!plan.highlight && plan.badge && (
                <div
                    className="absolute -top-3 left-7 bl-badge bl-badge-neutral"
                    data-testid={`plan-badge-${plan.id}`}
                >
                    {plan.badge}
                </div>
            )}
            <div className="text-[11px] tracking-[0.2em] uppercase text-bl-muted">
                {plan.suffix || "Subscription"}
            </div>
            <h3 className="mt-2 font-display font-semibold text-2xl text-bl">
                {plan.name}
            </h3>
            {plan.positioning && (
                <div className="mt-1 text-xs text-bl-primary font-medium tracking-wide">
                    {plan.positioning}
                </div>
            )}
            <div className="mt-3 flex items-baseline gap-1.5">
                <span className="font-display text-4xl font-semibold text-bl">
                    {plan.price}
                </span>
                {plan.per && (
                    <span className="text-bl-muted text-sm">{plan.per}</span>
                )}
                {plan.savingsBadge && (
                    <span
                        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--bl-green-soft)] text-[var(--bl-green)] text-[10px] tracking-[0.14em] uppercase font-medium"
                        data-testid={`plan-savings-${plan.id}`}
                    >
                        {plan.savingsBadge}
                    </span>
                )}
            </div>
            {plan.monthlyEq && (
                <div
                    className="mt-1 text-xs text-bl-muted font-mono-bl"
                    data-testid={`plan-monthly-eq-${plan.id}`}
                >
                    {plan.monthlyEq}
                </div>
            )}
            {plan.quota && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <div
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bl-teal-tint text-bl-primary text-[11px] tracking-[0.14em] uppercase font-medium"
                        data-testid={`plan-quota-${plan.id}`}
                    >
                        {plan.quota}
                    </div>
                    {plan.quotaBonus && (
                        <div
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--bl-green-soft)] text-[var(--bl-green)] text-[11px] tracking-[0.14em] uppercase font-medium"
                            data-testid={`plan-quota-bonus-${plan.id}`}
                        >
                            {plan.quotaBonus}
                        </div>
                    )}
                </div>
            )}
            {plan.coreValue && (
                <p
                    className="mt-4 text-[15px] text-bl leading-snug font-italic-accent"
                    data-testid={`plan-corevalue-${plan.id}`}
                >
                    “{plan.coreValue}”
                </p>
            )}
            {plan.tagline && (
                <p className="mt-2 text-sm text-bl-muted">{plan.tagline}</p>
            )}
            <ul className="mt-5 space-y-2.5 flex-1">
                {plan.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[15px] text-bl">
                        <Check className="w-4 h-4 text-bl-primary mt-1 flex-shrink-0" />
                        <span className="leading-relaxed">{f}</span>
                    </li>
                ))}
            </ul>
            <button
                onClick={() => onBuy(plan.id)}
                disabled={loading === plan.id}
                className={
                    "mt-7 " +
                    (plan.highlight ? "bl-btn-primary" : "bl-btn-secondary") +
                    " w-full"
                }
                data-testid={`plan-buy-${plan.id}-btn`}
            >
                {loading === plan.id ? "Redirecting…" : "Get started"}{" "}
                <ArrowUpRight className="w-4 h-4" />
            </button>
            {previewTier && onPreview && (
                <button
                    type="button"
                    onClick={() => onPreview(previewTier)}
                    className="mt-2.5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-medium text-bl-primary bg-bl-teal-tint hover:bg-bl-teal-tint/70 border border-bl-primary/15 transition-colors min-h-[44px]"
                    data-testid={`plan-preview-${previewTier}-btn`}
                >
                    <Eye className="w-4 h-4" />
                    View Sample {plan.name} Dashboard
                </button>
            )}
        </div>
    );
}

export default function Pricing() {
    const [loading, setLoading] = useState(null);
    const [billingInterval, setBillingInterval] = useState(() => {
        // Honor ?billing=annual deep links (used by the winback offer
        // email so the toggle lands in the right state).
        try {
            const params = new URLSearchParams(window.location.search);
            return params.get("billing") === "annual" ? "annual" : "monthly";
        } catch {
            return "monthly";
        }
    });
    // Sample-dashboard preview state. Supports `?preview=lite|pro|plus`
    // deep links so the marketing team / share links can land prospects
    // directly inside a tier preview.
    const [previewTier, setPreviewTier] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const t = params.get("preview");
            return ["lite", "pro", "plus"].includes(t) ? t : null;
        } catch {
            return null;
        }
    });
    // Side-by-side compare modal state. Deep-linkable via `?compare=`.
    const [compareCombo, setCompareCombo] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const c = params.get("compare");
            return COMPARE_COMBOS[c] ? c : null;
        } catch {
            return null;
        }
    });
    const navigate = useNavigate();
    const { user } = useAuth();
    const activeSubs = billingInterval === "annual" ? SUBS_ANNUAL : SUBS_MONTHLY;

    // Keep the URL in sync with the preview state so users can share links.
    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            if (previewTier) url.searchParams.set("preview", previewTier);
            else url.searchParams.delete("preview");
            if (compareCombo) url.searchParams.set("compare", compareCombo);
            else url.searchParams.delete("compare");
            window.history.replaceState({}, "", url.toString());
        } catch { /* non-critical */ }
    }, [previewTier, compareCombo]);

    const openPreview = (tier) => {
        track("investor_preview_button_clicked", { tier, source: "pricing_plan_card" });
        setPreviewTier(tier);
    };

    const openCompare = (combo) => {
        track("investor_compare_button_clicked", {
            combo,
            source: "pricing",
        });
        setCompareCombo(combo);
    };

    const handleBuy = async (id) => {
        try {
            setLoading(id);
            if (["summary", "closing_strategy"].includes(id)) {
                // need an upload first
                navigate("/upload?intent=" + id);
                return;
            }
            // subscriptions require auth
            if (!user) {
                navigate("/auth/login?next=/pricing");
                return;
            }
            const r = await createCheckout(id);
            window.location.href = r.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout.");
        } finally {
            setLoading(null);
        }
    };

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-16 md:pt-20 pb-12">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-7 min-w-0" data-reveal="slide-left-lg">
                            <div className="bl-eyebrow">Pricing</div>
                            <h1 className="mt-5 font-display font-semibold text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[1.02] text-bl max-w-3xl">
                                Honest pricing for{" "}
                                <span className="font-italic-accent text-bl-primary">
                                    strategic clarity.
                                </span>
                            </h1>
                            <p className="mt-6 text-bl-muted text-lg max-w-2xl leading-relaxed">
                                Buy once for a single inspection, or subscribe if you analyze
                                properties repeatedly. No hidden fees, no scarcity tricks.
                            </p>
                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <a
                                    href="#investor-plans"
                                    className="bl-btn-secondary bl-btn-sm"
                                    data-testid="pricing-hero-jump-investor"
                                >
                                    For investors →
                                </a>
                                <Link
                                    to="/upload"
                                    className="bl-btn-primary bl-btn-sm"
                                    data-testid="pricing-hero-upload"
                                >
                                    Upload your inspection
                                </Link>
                            </div>
                        </div>

                        {/* Right-side: starting-price + trust card */}
                        <div className="lg:col-span-5 min-w-0" data-reveal="slide-right" data-reveal-delay="2">
                            <div className="bl-card p-6 md:p-7" data-testid="pricing-hero-side">
                                <div className="flex items-center justify-between">
                                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                        <Receipt className="w-3.5 h-3.5" />
                                        Starting at
                                    </div>
                                    <div className="font-mono-bl text-xs text-bl-muted">
                                        One-time · No subscription
                                    </div>
                                </div>

                                <div className="mt-3 flex items-baseline gap-2">
                                    <span className="font-display text-5xl md:text-6xl font-semibold text-bl">
                                        $49
                                    </span>
                                    <span className="text-bl-muted text-sm">
                                        per inspection
                                    </span>
                                </div>
                                <p className="mt-2 text-sm text-bl-muted">
                                    Premium Closing Strategy at <span className="font-mono-bl text-bl">$99</span>.
                                    Investor subscriptions start at <span className="font-mono-bl text-bl">$29/mo</span>.
                                </p>

                                <div className="mt-6 space-y-2">
                                    {[
                                        [Lock, "Private PDF upload — tied to your account"],
                                        [Repeat, "Pay once, re-open anytime from dashboard"],
                                        [ShieldCheckIcon, "Secure checkout. Cancel subscriptions in one click."],
                                        [Zap, "Report ready in minutes — not days"],
                                    ].map(([Ico, label], i) => (
                                        <div
                                            key={label}
                                            className="flex items-start gap-3 text-sm text-bl"
                                            data-reveal="rise-sm"
                                            data-reveal-delay={Math.min(5, i + 1)}
                                        >
                                            <Ico className="w-4 h-4 text-bl-primary mt-0.5 shrink-0" />
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bl-container pb-16" data-reveal>
                <div className="bl-eyebrow">For homebuyers</div>
                <h2 className="mt-3 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl">
                    One-time inspection analysis
                </h2>
                <div className="mt-8 grid md:grid-cols-2 gap-6">
                    {ONE_TIME.map((p, i) => (
                        <PlanCard
                            key={p.id}
                            plan={p}
                            onBuy={handleBuy}
                            loading={loading}
                            index={i}
                        />
                    ))}
                </div>
                <p className="mt-5 text-sm text-bl-muted max-w-2xl">
                    Already purchased Inspection Summary? You can upgrade to Closing
                    Strategy later for{" "}
                    <span className="font-mono-bl text-bl">$50</span> — only the
                    difference. We never ask you to re-purchase something you already
                    own.{" "}
                    <span className="text-bl">
                        Not sure which to pick? Start with Inspection Summary
                        and upgrade later for $50.
                    </span>
                </p>

                {/* Trust line — secure checkout + refund-style reassurance */}
                <div
                    className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-bl-muted"
                    data-testid="pricing-trust-row"
                >
                    <span className="inline-flex items-center gap-1.5">
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-bl-primary" />
                        Secure checkout by Stripe — card details handled by Stripe.
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-bl-primary" />
                        No hidden subscription on one-time reports.
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-bl-primary" />
                        If your paid report fails to generate, your access is
                        preserved and we'll help fix it.
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-bl-primary" />
                        Your inspection PDF is never shared with contractors by default.
                    </span>
                </div>

                <p
                    className="mt-3 text-xs text-bl-muted max-w-3xl leading-relaxed"
                    data-testid="pricing-disclaimer"
                >
                    Buyer's Leverage provides decision-support analysis, not
                    legal, engineering, inspection, or contractor advice. A
                    single seller credit or repair concession can often exceed
                    the cost of the report — though savings are never
                    guaranteed.
                </p>

                {/* Side-by-side comparison */}
                <div className="mt-12 bl-card overflow-x-auto" data-testid="pricing-comparison-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left">
                                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Feature
                                </th>
                                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center">
                                    Inspection Summary
                                    <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl">$49</div>
                                </th>
                                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center bg-bl-teal-tint/30">
                                    <span className="text-bl-primary">Closing Strategy · Premium</span>
                                    <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl">$99</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARISON_ROWS.map((g) => (
                                <Fragment key={g.group}>
                                    <tr className="bg-bl-bg/40">
                                        <td colSpan={3} className="px-5 py-3 text-[11px] tracking-[0.18em] uppercase text-bl-primary font-medium border-b border-bl">
                                            {g.group}
                                        </td>
                                    </tr>
                                    {g.rows.map(([label, std, prem]) => (
                                        <tr key={label} className="border-b border-bl last:border-0">
                                            <td className="px-5 py-3 text-bl">{label}</td>
                                            <td className="px-5 py-3 text-center">
                                                {std ? (
                                                    <Check className="w-4 h-4 text-bl-primary inline" />
                                                ) : (
                                                    <span className="text-bl-muted">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-center bg-bl-teal-tint/15">
                                                {prem ? (
                                                    <Check className="w-4 h-4 text-bl-primary inline" />
                                                ) : (
                                                    <span className="text-bl-muted">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="bl-container pb-24" id="investor-plans" data-reveal>
                <div className="bl-eyebrow">For investors</div>
                <h2 className="mt-3 font-display font-semibold text-3xl md:text-4xl tracking-tight text-bl">
                    A workflow tier for every kind of investor.
                </h2>
                <p className="mt-4 text-bl-muted text-base max-w-2xl leading-relaxed">
                    Lite to understand a report. Pro to compare deals and
                    negotiate smarter. Plus to operate inspection analysis like
                    a serious workflow system.
                </p>

                {/* Monthly / Annual billing toggle */}
                <div className="mt-7 inline-block">
                    <SlidingTabs
                        active={billingInterval}
                        onChange={setBillingInterval}
                        variant="pill"
                        testId="billing-interval"
                        ariaLabel="Billing interval"
                        tabs={[
                            { id: "monthly", label: "Monthly" },
                            {
                                id: "annual",
                                label: "Annual",
                                badge: (
                                    <span
                                        className={
                                            "inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] tracking-[0.14em] uppercase font-medium whitespace-nowrap transition-colors duration-100 delay-200 " +
                                            (billingInterval === "annual"
                                                ? "bg-bl-cream/20 text-bl-cream"
                                                : "bg-[var(--bl-green-soft)] text-[var(--bl-green)]")
                                        }
                                    >
                                        Get 2 Months Free
                                    </span>
                                ),
                            },
                        ]}
                    />
                </div>

                {/* "Annual Members Receive" panel removed — perks are now shown
                    directly on each annual plan card (green "+N reports / month"
                    bonus pill next to the base quota). Keeps the pricing page
                    decision-focused and avoids restating the same benefit twice. */}

                <div className="mt-8 grid md:grid-cols-3 gap-6">
                    {activeSubs.map((p, i) => (
                        <PlanCard
                            key={p.id}
                            plan={p}
                            onBuy={handleBuy}
                            onPreview={openPreview}
                            loading={loading}
                            index={i}
                        />
                    ))}
                </div>
                <p className="mt-5 text-sm text-bl-muted max-w-2xl">
                    Monthly usage is based on{" "}
                    <span className="text-bl">generated</span> reports — not
                    currently stored reports. Deleting a report doesn't restore
                    a slot. Cancel or change anytime through your billing
                    portal.
                </p>

                <div className="mt-6">
                    <CompareTiersButton
                        onClick={() => openCompare("lite-vs-pro")}
                        label="Compare two tiers side-by-side"
                        testid="pricing-compare-tiers-btn"
                    />
                </div>

                {/* Annual savings explainer removed — the per-plan "+X reports / month"
                    green pill on each annual card already conveys the upgrade reward.
                    Keeping the page tighter and reducing decision-paralysis copy. */}

                {/* Feature-grouped Lite vs Pro vs Plus comparison */}
                <div
                    className="mt-12 bl-card overflow-x-auto"
                    data-testid="investor-comparison-table"
                >
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left">
                                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Feature
                                </th>
                                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center">
                                    Investor Lite
                                    <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl" data-testid="investor-header-price-lite">
                                        {billingInterval === "annual" ? "$290 / yr" : "$29 / mo"}
                                    </div>
                                </th>
                                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center bg-bl-teal-tint/30">
                                    <span className="text-bl-primary">
                                        Investor Pro
                                    </span>
                                    <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl" data-testid="investor-header-price-pro">
                                        {billingInterval === "annual" ? "$790 / yr" : "$79 / mo"}
                                    </div>
                                    <div className="mt-0.5 text-[9px] tracking-[0.14em] uppercase text-bl-primary">
                                        Best for active investors
                                    </div>
                                </th>
                                <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-center">
                                    Investor Plus
                                    <div className="mt-1 text-bl text-xs normal-case tracking-normal font-mono-bl" data-testid="investor-header-price-plus">
                                        {billingInterval === "annual" ? "$1,490 / yr" : "$149 / mo"}
                                    </div>
                                    <div className="mt-0.5 text-[9px] tracking-[0.14em] uppercase text-bl-muted">
                                        High-volume workflows
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Dynamic quota row — reacts to the existing
                                monthly/annual toggle higher up the page. */}
                            <Fragment>
                                <tr className="bg-bl-bg/40">
                                    <td
                                        colSpan={4}
                                        className="px-5 py-3 text-[11px] tracking-[0.18em] uppercase text-bl-primary font-medium border-b border-bl"
                                    >
                                        Included reports
                                    </td>
                                </tr>
                                <tr className="border-b border-bl" data-testid="investor-comparison-quota-row">
                                    <td className="px-5 py-3 text-bl">
                                        {billingInterval === "annual"
                                            ? "Reports per month (annual bonus included)"
                                            : "Reports per month"}
                                    </td>
                                    {[
                                        { monthly: 3, annual: 5, bonusPerYear: 24 },
                                        { monthly: 10, annual: 15, bonusPerYear: 60 },
                                        { monthly: 25, annual: 35, bonusPerYear: 120 },
                                    ].map((q, idx) => {
                                        const isPro = idx === 1;
                                        const base = billingInterval === "annual" ? q.annual : q.monthly;
                                        return (
                                            <td
                                                key={idx}
                                                className={
                                                    "px-5 py-3 text-center " +
                                                    (isPro ? "bg-bl-teal-tint/15" : "")
                                                }
                                                data-testid={`investor-quota-cell-${idx}`}
                                            >
                                                <div className="inline-flex items-center justify-center gap-2 flex-wrap">
                                                    <span className="font-mono-bl text-bl text-sm tnum">
                                                        {base}/mo
                                                    </span>
                                                    {billingInterval === "annual" && (
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full bg-bl-primary/10 text-bl-primary text-[10px] font-medium px-2 py-0.5 border border-bl-primary/30"
                                                            data-testid={`investor-quota-bonus-${idx}`}
                                                        >
                                                            +{q.bonusPerYear}/yr
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </Fragment>

                            {INVESTOR_COMPARISON.map((g) => (
                                <Fragment key={g.group}>
                                    <tr className="bg-bl-bg/40">
                                        <td
                                            colSpan={4}
                                            className="px-5 py-3 text-[11px] tracking-[0.18em] uppercase text-bl-primary font-medium border-b border-bl"
                                        >
                                            {g.group}
                                        </td>
                                    </tr>
                                    {g.rows.map(([label, lite, pro, plus]) => (
                                        <tr
                                            key={label}
                                            className="border-b border-bl last:border-0"
                                        >
                                            <td className="px-5 py-3 text-bl">
                                                {label}
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                {lite ? (
                                                    <Check className="w-4 h-4 text-bl-primary inline" />
                                                ) : (
                                                    <span className="text-bl-muted">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-center bg-bl-teal-tint/15">
                                                {pro ? (
                                                    <Check className="w-4 h-4 text-bl-primary inline" />
                                                ) : (
                                                    <span className="text-bl-muted">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                {plus ? (
                                                    <Check className="w-4 h-4 text-bl-primary inline" />
                                                ) : (
                                                    <span className="text-bl-muted">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Investor reassurance row */}
                <div
                    className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-bl-muted"
                    data-testid="investor-trust-row"
                >
                    <span className="inline-flex items-center gap-1.5">
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-bl-primary" />
                        Cancel or switch tiers anytime — no annual commitment.
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-bl-primary" />
                        No setup fees. No per-seat charges on v1.
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-bl-primary" />
                        Your inspection PDFs are never shared with contractors by default.
                    </span>
                </div>
            </section>
            <InvestorDashboardPreview
                tier={previewTier || "lite"}
                open={!!previewTier}
                onOpenChange={(o) => {
                    if (!o) setPreviewTier(null);
                }}
                source="pricing"
                onCompareTiers={(combo) => setCompareCombo(combo)}
            />
            <InvestorComparePreview
                combo={compareCombo || "lite-vs-pro"}
                open={!!compareCombo}
                onOpenChange={(o) => {
                    if (!o) setCompareCombo(null);
                }}
                source="pricing"
            />
        </PublicLayout>
    );
}
